import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import type { Level, Point } from "../game/types";
import { buildRoom, type RoomRig } from "./room";
import { buildCart, buildGem, buildHero, CELL } from "./loot";
import { buildMinotaur, type Minotaur, type MinotaurAnim } from "./minotaur";

/**
 * The 3D stage for Cart & Run: a torch-lit dungeon chamber with a mine cart in
 * the middle, loot scattered on the grid, and a minotaur marching down the long
 * hallway behind the doorway as the step budget burns down. Owns the renderer,
 * camera, lights and animation loop and exposes a small imperative API the game
 * logic drives. All RULES live in src/game / @jpjj/solver-core.
 */

interface Tween { start: number; dur: number; tick: (k: number) => void; done?: () => void }
const easeInOut = (k: number) => (k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2);
const reducedMotion = typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;

const TIER = (level: Level, prize: number): number => {
  const max = Math.max(...level.loot.map((l) => l.prize));
  const min = Math.min(...level.loot.map((l) => l.prize));
  if (max === min) return 1;
  const t = (prize - min) / (max - min);
  return t > 0.66 ? 2 : t > 0.33 ? 1 : 0;
};

export class Stage {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  readonly controls: OrbitControls;

  private host: HTMLElement;
  private roomGroup = new THREE.Group();
  private lootGroup = new THREE.Group();
  private trailGroup = new THREE.Group();
  private rig: RoomRig | null = null;
  private hero: THREE.Group | null = null;
  private minotaur: Minotaur;
  private gems = new Map<number, THREE.Group>();

  private level: Level | null = null;
  private heroPos = new THREE.Vector3();    // current visual hero world position
  private tweens: Tween[] = [];
  private raycaster = new THREE.Raycaster();
  private clock = new THREE.Clock();
  private t0 = performance.now();
  private danger = 0;
  private terminalAnim: MinotaurAnim | null = null;

  constructor(host: HTMLElement) {
    this.host = host;
    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.08;
    host.appendChild(this.renderer.domElement);

    this.scene.background = this.gradient("#241a10", "#080503");
    this.scene.fog = new THREE.FogExp2(0x080503, 0.038);

    this.camera = new THREE.PerspectiveCamera(46, 1, 0.1, 120);
    // a mostly head-on, slightly raised view so the camera looks straight down
    // the centred hallway and the minotaur is framed by the doorway throughout
    this.camera.position.set(2.4, 4.4, 9);

    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enablePan = false;
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 0.08;
    this.controls.minPolarAngle = 0.5;
    this.controls.maxPolarAngle = 1.32;
    this.controls.target.set(0, 0.75, -0.6);

    /* lights: dim warm ambient + a soft key; the torches do the real work */
    this.scene.add(new THREE.HemisphereLight(0x5a4730, 0x140d07, 0.75));
    const key = new THREE.DirectionalLight(0xffe6c0, 1.0);
    key.position.set(4, 10, 7);
    key.castShadow = true;
    key.shadow.mapSize.set(2048, 2048);
    key.shadow.camera.near = 1; key.shadow.camera.far = 40;
    key.shadow.camera.left = -10; key.shadow.camera.right = 10;
    key.shadow.camera.top = 10; key.shadow.camera.bottom = -10;
    key.shadow.bias = -0.0005;
    this.scene.add(key);

    this.scene.add(this.roomGroup);
    this.roomGroup.add(this.trailGroup);
    this.roomGroup.add(this.lootGroup);

    this.minotaur = buildMinotaur(0.42);
    this.scene.add(this.minotaur.root);

    this.resize();
    addEventListener("resize", () => this.resize());
    this.renderer.setAnimationLoop(() => this.frame());
  }

  private gradient(top: string, bottom: string): THREE.Texture {
    const c = document.createElement("canvas");
    c.width = 2; c.height = 256;
    const ctx = c.getContext("2d")!;
    const g = ctx.createLinearGradient(0, 0, 0, 256);
    g.addColorStop(0, top); g.addColorStop(1, bottom);
    ctx.fillStyle = g; ctx.fillRect(0, 0, 2, 256);
    const t = new THREE.CanvasTexture(c);
    t.colorSpace = THREE.SRGBColorSpace;
    return t;
  }

  resize(): void {
    const w = this.host.clientWidth, h = Math.max(this.host.clientHeight, 1);
    this.renderer.setSize(w, h);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    if (this.level) this.fitCamera();
  }

  private fitCamera(): void {
    if (!this.level) return;
    const span = Math.max(this.level.cols, this.level.rows + 5) * CELL;
    const aspect = Math.max(this.camera.aspect, 0.01);
    const portrait = Math.min(2.1, Math.max(1, 1.12 / Math.pow(aspect, 0.9)));
    const dist = span * 0.92 * portrait;
    this.controls.minDistance = dist * 0.55;
    this.controls.maxDistance = dist * 1.7;
    const dir = this.camera.position.clone().sub(this.controls.target);
    if (dir.lengthSq() < 1e-4) dir.set(0.5, 0.9, 1);
    dir.normalize();
    this.camera.position.copy(this.controls.target).addScaledVector(dir, dist);
    this.controls.update();
  }

  /* ---------------- room lifecycle ---------------- */

  loadRoom(level: Level): void {
    this.level = level;
    this.terminalAnim = null;
    this.danger = 0;
    // tear down the old room
    this.disposeGroup(this.roomGroup, [this.trailGroup, this.lootGroup]);
    this.clearGroup(this.lootGroup);
    this.clearGroup(this.trailGroup);
    this.gems.clear();
    if (this.rig) this.roomGroup.remove(this.rig.group);

    this.rig = buildRoom(level.cols, level.rows);
    this.roomGroup.add(this.rig.group);

    // loot gems
    level.loot.forEach((l, k) => {
      const idx = k + 1;
      const gem = buildGem(idx, l.prize, l.weight, TIER(level, l.prize));
      const [wx, wz] = this.rig!.cellToWorld(level.points[idx][0], level.points[idx][1]);
      gem.position.set(wx, 0, wz);
      this.lootGroup.add(gem);
      this.gems.set(idx, gem);
    });

    // mine cart on the depot cell
    const cart = buildCart();
    const [cx, cz] = this.rig.cellToWorld(level.points[0][0], level.points[0][1]);
    cart.position.set(cx, 0, cz);
    this.roomGroup.add(cart);

    // hero starts on the cart
    if (this.hero) this.roomGroup.remove(this.hero);
    this.hero = buildHero();
    this.heroPos.set(cx, 0, cz);
    this.hero.position.copy(this.heroPos);
    this.hero.visible = true;
    this.roomGroup.add(this.hero);

    // minotaur waits far down the hallway, facing the room (+z)
    this.minotaur.root.position.set(0, 0, this.rig.zFar);
    this.minotaur.root.rotation.y = 0;
    this.minotaur.setAnim("idle");

    this.tweens = [];
    this.fitCamera();
  }

  /* ---------------- play API ---------------- */

  private cellWorld(cell: Point): THREE.Vector3 {
    const [x, z] = this.rig!.cellToWorld(cell[0], cell[1]);
    return new THREE.Vector3(x, 0, z);
  }

  /** Tween the hero from where it stands to `cell` along a Manhattan L-path. */
  heroTo(cell: Point, dur = 260): Promise<void> {
    if (!this.hero || !this.rig) return Promise.resolve();
    const start = this.heroPos.clone();
    const end = this.cellWorld(cell);
    const lenX = Math.abs(end.x - start.x), lenZ = Math.abs(end.z - start.z);
    const total = lenX + lenZ;
    this.heroPos.copy(end);
    if (total < 1e-4 || reducedMotion) {
      this.hero.position.copy(end);
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      this.tween(Math.max(160, dur * (total / 2)), (k) => {
        const d = k * total;
        const x = d <= lenX ? start.x + Math.sign(end.x - start.x) * d : end.x;
        const z = d <= lenX ? start.z : start.z + Math.sign(end.z - start.z) * (d - lenX);
        this.hero!.position.set(x, 0, z);
      }, resolve);
    });
  }

  /** Pop a collected gem off the floor (hauled away). */
  collectLoot(idx: number): void {
    const g = this.gems.get(idx);
    if (!g) return;
    if (reducedMotion) { g.visible = false; return; }
    const base = g.scale.x;
    this.tween(220, (k) => {
      const s = base * (1 - k);
      g.scale.setScalar(Math.max(0.0001, s));
      g.position.y = k * 0.6;
    }, () => { g.visible = false; g.position.y = 0; g.scale.setScalar(base); });
  }

  /** Restore all gems to the floor (used when re-running a room). */
  resetLoot(): void {
    for (const g of this.gems.values()) { g.visible = true; g.scale.setScalar(1); g.position.y = 0; }
  }

  /** Brighten the hero's carry-glow with the load it hauls. */
  setCarry(load: number, capacity: number): void {
    const l = this.hero?.userData.carryLight as THREE.PointLight | undefined;
    if (l) l.intensity = load > 0 ? 0.8 + 1.8 * Math.min(1, load / capacity) : 0;
  }

  /** A burst of gold coins over the cart when a trip is banked. */
  bankFlash(cell: Point): void {
    if (reducedMotion || !this.rig) return;
    const [cx, cz] = this.rig.cellToWorld(cell[0], cell[1]);
    const mat = new THREE.MeshStandardMaterial({ color: 0xe7bd5c, emissive: 0x6a4a10, emissiveIntensity: 0.8, metalness: 0.8, roughness: 0.3 });
    for (let i = 0; i < 8; i++) {
      const coin = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.02, 10), mat);
      const a = Math.random() * Math.PI * 2, r = 0.1 + Math.random() * 0.1;
      coin.position.set(cx + Math.cos(a) * r, 0.7, cz + Math.sin(a) * r);
      coin.rotation.set(Math.random(), Math.random(), Math.random());
      this.roomGroup.add(coin);
      const vy = 1.4 + Math.random() * 0.8, vx = (Math.random() - 0.5) * 1.2, vz = (Math.random() - 0.5) * 1.2;
      this.tween(620, (k) => {
        coin.position.set(cx + vx * k, 0.7 + vy * k - 4.2 * k * k, cz + vz * k);
        coin.rotation.y += 0.3;
        (coin.material as THREE.MeshStandardMaterial).opacity = 1 - k;
        (coin.material as THREE.MeshStandardMaterial).transparent = true;
      }, () => { this.roomGroup.remove(coin); coin.geometry.dispose(); });
    }
  }

  /** Draw the trail: completed trips faint, the current leg bright. */
  setTrail(level: Level, trips: number[][], leg: number[]): void {
    this.clearGroup(this.trailGroup);
    for (let ti = 0; ti < trips.length; ti++)
      this.drawTrip(level, trips[ti], 0x6f5a2c, 0.34, ti);
    this.drawTrip(level, leg, 0xe7bd5c, 0.85, -1);
  }

  /** Coloured trips for the reveal (one hue per trip). */
  setRevealTrail(level: Level, trips: number[][]): void {
    const hues = [0xf3c768, 0x6fd3c0, 0x7fb6ff, 0xe2b3f0];
    this.clearGroup(this.trailGroup);
    trips.forEach((t, i) => this.drawTrip(level, t, hues[i % hues.length], 0.85, i));
  }

  private drawTrip(level: Level, trip: number[], color: number, opacity: number, _ti: number): void {
    for (let i = 0; i + 1 < trip.length; i++) {
      const a = level.points[trip[i]], b = level.points[trip[i + 1]];
      const [ax, az] = this.rig!.cellToWorld(a[0], a[1]);
      const [bx, bz] = this.rig!.cellToWorld(b[0], b[1]);
      this.ribbon(ax, az, bx, az, color, opacity); // x-run
      this.ribbon(bx, az, bx, bz, color, opacity); // z-run
    }
  }

  private ribbon(ax: number, az: number, bx: number, bz: number, color: number, opacity: number): void {
    const len = Math.hypot(bx - ax, bz - az);
    if (len < 1e-4) return;
    const geo = new THREE.PlaneGeometry(len + 0.06, 0.07);
    const m = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({
      color, transparent: true, opacity, depthWrite: false, side: THREE.DoubleSide }));
    m.rotation.x = -Math.PI / 2;
    m.position.set((ax + bx) / 2, 0.02, (az + bz) / 2);
    m.rotation.z = -Math.atan2(bz - az, bx - ax);
    this.trailGroup.add(m);
  }

  /** Map the step budget (0..1) to the minotaur's march down the hallway. */
  setDanger(frac: number): void {
    if (!this.rig) return;
    this.danger = Math.max(0, Math.min(1, frac));
    const zStop = this.rig.zBack - 0.4;       // halts just outside the doorway
    this.minotaur.root.position.z = this.rig.zFar + (zStop - this.rig.zFar) * this.danger;
    if (!this.terminalAnim) this.minotaur.setAnim(this.danger > 0.02 ? "walk" : "idle");
  }

  /** The beast reaches the room and lunges. */
  onCaught(): void {
    if (!this.rig) return;
    this.terminalAnim = "attack";
    this.minotaur.root.position.z = this.rig.zBack - 0.4;
    this.minotaur.setAnim("attack");
  }

  /** The hero flees / is clear; the beast arrives and roars in frustration. */
  onArrivedSafe(): void {
    if (!this.rig) return;
    this.terminalAnim = "roar";
    this.minotaur.root.position.z = this.rig.zBack - 0.5;
    this.minotaur.setAnim("roar");
  }

  /** Resume normal marching (room reset / re-entered). */
  clearTerminal(): void {
    this.terminalAnim = null;
    this.minotaur.setAnim(this.danger > 0.02 ? "walk" : "idle");
  }

  /* ---------------- reveal ---------------- */

  setRevealMode(on: boolean): void {
    this.controls.autoRotate = on && !reducedMotion;
    this.controls.autoRotateSpeed = 0.55;
    if (on) {
      this.terminalAnim = "idle";
      this.minotaur.setAnim("idle");
      if (this.rig) this.minotaur.root.position.z = this.rig.zBack - 3.2; // looms in the doorway
    }
  }

  /** Replay a set of trips: walk the hero each leg, pop loot, build a coloured
   *  trail. `onTrip(n)` fires as each trip completes (n trips shown). Resolves
   *  when finished or when `token.cancelled` flips true. */
  async replayTrips(level: Level, trips: number[][], token: { cancelled: boolean },
    onTrip?: (n: number) => void): Promise<void> {
    this.resetLoot();
    this.clearGroup(this.trailGroup);
    const cart = level.points[0];
    this.heroPos.copy(this.cellWorld(cart));
    if (this.hero) this.hero.position.copy(this.heroPos);
    onTrip?.(0);
    const shown: number[][] = [];
    for (const trip of trips) {
      if (token.cancelled) return;
      const partial: number[] = [0];
      for (let i = 1; i < trip.length; i++) {
        if (token.cancelled) return;
        const node = trip[i];
        await this.heroTo(level.points[node], 300);
        if (token.cancelled) return;
        partial.push(node);
        if (node !== 0) this.collectLoot(node);
        this.setRevealTrail(level, [...shown, partial]);
        await this.delay(70);
      }
      shown.push(trip);
      onTrip?.(shown.length);
    }
    this.setRevealTrail(level, trips);
  }

  /** Snap to a finished set of trips with no animation (skip). */
  showTripsInstant(level: Level, trips: number[][]): void {
    this.resetLoot();
    for (const t of trips) for (const n of t) if (n !== 0) { const g = this.gems.get(n); if (g) g.visible = false; }
    this.setRevealTrail(level, trips);
    const cart = level.points[0];
    this.heroPos.copy(this.cellWorld(cart));
    if (this.hero) this.hero.position.copy(this.heroPos);
  }

  /* ---------------- picking ---------------- */

  private castAt(clientX: number, clientY: number, targets: THREE.Object3D[]): THREE.Intersection | null {
    const r = this.renderer.domElement.getBoundingClientRect();
    const ndc = new THREE.Vector2(((clientX - r.left) / r.width) * 2 - 1, -((clientY - r.top) / r.height) * 2 + 1);
    this.raycaster.setFromCamera(ndc, this.camera);
    return this.raycaster.intersectObjects(targets, true)[0] ?? null;
  }

  /** The loot node under the pointer, if any. */
  pickLoot(clientX: number, clientY: number): number | null {
    const hit = this.castAt(clientX, clientY, [this.lootGroup]);
    if (!hit) return null;
    let o: THREE.Object3D | null = hit.object;
    while (o && o.userData.lootIdx === undefined) o = o.parent;
    return o ? (o.userData.lootIdx as number) : null;
  }

  /** Whether the pointer is over the mine cart. */
  pickCart(clientX: number, clientY: number): boolean {
    const hit = this.castAt(clientX, clientY, [this.roomGroup]);
    if (!hit) return false;
    let o: THREE.Object3D | null = hit.object;
    while (o && !o.userData.isCart) o = o.parent;
    return !!o;
  }

  /** Screen position (CSS px) of a cell centre, for the headless verifier. */
  cellToScreen(cell: Point, y = 0.4): { x: number; y: number } {
    const w = this.cellWorld(cell);
    const v = new THREE.Vector3(w.x, y, w.z).project(this.camera);
    const r = this.renderer.domElement.getBoundingClientRect();
    return { x: r.left + ((v.x + 1) / 2) * r.width, y: r.top + ((1 - v.y) / 2) * r.height };
  }

  /* ---------------- internals ---------------- */

  private tween(dur: number, tick: (k: number) => void, done?: () => void): void {
    this.tweens.push({ start: performance.now(), dur, tick, done });
  }
  private delay(ms: number): Promise<void> {
    return new Promise((res) => this.tween(ms, () => {}, res));
  }

  private clearGroup(g: THREE.Group): void {
    for (let i = g.children.length - 1; i >= 0; i--) {
      const c = g.children[i];
      g.remove(c);
      c.traverse((o) => { if (o instanceof THREE.Mesh) o.geometry.dispose(); });
    }
  }
  private disposeGroup(_g: THREE.Group, _keep: THREE.Group[]): void { /* room group children disposed via clear on rebuild */ }

  private frame(): void {
    const dt = Math.min(this.clock.getDelta(), 0.05);
    const t = (performance.now() - this.t0) / 1000;

    for (let i = this.tweens.length - 1; i >= 0; i--) {
      const tw = this.tweens[i];
      const k = Math.min(1, (performance.now() - tw.start) / tw.dur);
      tw.tick(easeInOut(k));
      if (k >= 1) { this.tweens.splice(i, 1); tw.done?.(); }
    }

    this.minotaur.update(dt);

    if (!reducedMotion && this.rig) {
      // torch + hero-flame flicker
      const flick = 0.85 + Math.sin(t * 11) * 0.08 + Math.sin(t * 23.3) * 0.05;
      for (const fl of this.rig.flames) fl.scale.set(1, 1.8 * flick, 1);
      for (const li of this.rig.torchLights) {
        const base = (li.userData.base ??= li.intensity) as number;
        li.intensity = base * flick;
      }
      const hf = this.hero?.userData.flame as THREE.Mesh | undefined;
      if (hf) hf.scale.set(1, 1.5 * flick, 1);
    }

    this.controls.update();
    this.renderer.render(this.scene, this.camera);
  }
}
